import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../App';

export const WIN_TONE = { prime: 'bg-emerald-100 text-emerald-700 border-emerald-200', aging: 'bg-amber-100 text-amber-700 border-amber-200', early: 'bg-sky-100 text-sky-700 border-sky-200', overdue: 'bg-rose-100 text-rose-700 border-rose-200', unknown: 'bg-slate-100 text-slate-600 border-slate-200', has_solar: 'bg-violet-100 text-violet-700 border-violet-200' };
const SEV = { Extreme: 'bg-rose-600 text-white', Severe: 'bg-rose-100 text-rose-700', Moderate: 'bg-amber-100 text-amber-700', Minor: 'bg-slate-100 text-slate-600' };

export default function StormRoofAge() {
  const [addr, setAddr] = useState({ address: '', city: '', state: '', zip: '' });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!addr.address.trim()) { toast.error('Enter a street address first'); return; }
    setBusy(true);
    try {
      const r = await api.get('/api/storms/roof-lookup', { params: addr });
      setRes(r.data);
      if (r.data.quota_exceeded) toast.error('Permit-network quota reached — storm alerts shown, roof age may be estimated', { duration: 6000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Roof-age lookup failed'); setRes(null); }
    setBusy(false);
  };

  const field = (k, label, ph, w, extra = {}) => (
    <div className={w}><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{label}</label>
      <input value={addr[k]} onChange={e => setAddr({ ...addr, [k]: extra.upper ? e.target.value.toUpperCase().slice(0, 2) : e.target.value })} placeholder={ph} className="input-field" data-testid={`storm-roof-${k}`} /></div>
  );

  return (
    <div className="space-y-4" data-testid="storm-roof-age-panel">
      <form onSubmit={lookup} className="p-4 rounded-2xl border border-border bg-surface flex flex-wrap items-end gap-3">
        {field('address', 'Street address *', '17 Pierce St', 'flex-1 min-w-[220px]')}
        {field('city', 'City', 'West Yarmouth', 'w-40')}
        {field('state', 'State', 'MA', 'w-24', { upper: true })}
        {field('zip', 'ZIP', '02673', 'w-28')}
        <button type="submit" disabled={busy} className="btn-primary" data-testid="storm-roof-lookup-btn">
          {busy ? <i className="fa-solid fa-spinner fa-spin mr-2" /> : <i className="fa-solid fa-house-chimney-crack mr-2" />}Check roof age + storms</button>
      </form>

      {res && (
        <div className="space-y-3" data-testid="storm-roof-result">
          <div className={`p-4 rounded-2xl border text-sm font-semibold ${res.roof_damage_alerts ? 'border-rose-300 bg-rose-50 text-rose-800' : res.in_storm_area ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-border bg-surface text-text-secondary'}`} data-testid="storm-roof-verdict">
            <i className={`fa-solid ${res.in_storm_area ? 'fa-triangle-exclamation' : 'fa-cloud-sun'} mr-2`} />{res.storm_verdict}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <RoofCard res={res} />
            <AlertsCard res={res} />
          </div>
        </div>
      )}
    </div>
  );
}

function RoofCard({ res }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-surface">
      <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Roof age</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-heading text-4xl font-black text-text-primary" data-testid="storm-roof-age">{res.roof_age_years != null ? `${res.roof_age_years} yrs` : 'Unknown'}</span>
        {res.roof_window && <span className={`badge border ${WIN_TONE[res.roof_window] || WIN_TONE.unknown}`} data-testid="storm-roof-window">{res.roof_window}</span>}
        {res.roof_age_estimated && <span className="badge bg-amber-50 text-amber-700 border-amber-200">estimated</span>}
        {res.has_existing_solar && <span className="badge bg-violet-100 text-violet-700 border-violet-200">has solar</span>}
      </div>
      <p className="text-xs text-text-muted mt-1">{res.address}</p>
      <p className="text-[11px] text-text-muted mt-1" data-testid="storm-roof-source"><i className="fa-solid fa-database mr-1" />{res.roof_age_source || res.permit_network_note || 'No roofing permit or year-built on record'}</p>
      {res.roof_permit_date && <p className="text-[11px] text-text-muted">Last roof permit: <b>{res.roof_permit_date}</b>{res.year_built ? ` · built ${res.year_built}` : ''}</p>}
      {res.roof_window_copy && <p className="text-sm text-text-secondary mt-2">{res.roof_window_copy}</p>}
      {res.owner_name && <p className="text-xs text-text-muted mt-1"><i className="fa-solid fa-user mr-1" />{res.owner_name}</p>}
      {(res.permits || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {res.permits.map((p, i) => (
            <div key={i} className="text-[11px] text-text-muted flex gap-2" data-testid="storm-roof-permit"><b className="text-text-secondary">{p.issue_date || '—'}</b><span className="truncate">{p.type || p.description || 'permit'}</span></div>
          ))}
        </div>
      )}
      {res.lead && <Link to={`/leads/${res.lead.id}`} className="inline-block mt-3 text-xs font-semibold text-primary hover:underline" data-testid="storm-roof-lead-link"><i className="fa-solid fa-address-card mr-1" />Already a lead: {res.lead.name} (roof age saved)</Link>}
    </div>
  );
}

function AlertsCard({ res }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-surface">
      <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Active storm alerts at this home ({res.alert_count})</p>
      {!res.alerts?.length && <p className="text-sm text-text-muted" data-testid="storm-roof-no-alert"><i className="fa-solid fa-cloud-sun text-sky-400 mr-2" />No active NWS alert over this address right now.</p>}
      <div className="space-y-2">
        {(res.alerts || []).map(a => (
          <div key={a.id} className="text-xs bg-surface-alt border border-border rounded-lg px-3 py-2" data-testid="storm-roof-alert">
            <div className="flex items-center gap-2"><span className={`badge ${SEV[a.severity] || SEV.Minor}`}>{a.severity}</span><b className="text-text-primary">{a.event}</b></div>
            <p className="text-text-muted mt-1">{a.headline}</p>
            {a.ends && <p className="text-[10px] text-text-muted mt-0.5">until {new Date(a.ends).toLocaleString()}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
